import React, { FC, Suspense } from 'react';

import {
  AppstoreOutlined,
  BarChartOutlined,
  BuildOutlined,
  FileProtectOutlined,
  FolderOpenOutlined,
  IdcardOutlined,
  ImportOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProfileOutlined,
  SnippetsOutlined,
  ToolOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { Badge, Button, Layout, Menu, Spin, theme, Typography } from 'antd';
import i18n from 'i18next';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { setupDayJs } from '@app/services/date';
import { useEpdSigningCountQuery } from '@app/services/hooks/useEpdSigningCountQuery';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { stringToBoolean } from '@app/services/stringToBoolean';
import { selectIsCounterpartySpace } from '@app/store/currentSessionReducer';
import { AccessDenied } from '@components/AccessDenied';

import { UserMenu } from './components/UserMenu';
import { WorkspaceLogo } from './components/WorkspaceLogo';

import { Container, LogoContainer, SpaceCaption, SpinnerContainer } from './styles';

enum MenuKeys {
  PRODUCTS = '/dashboard/products',
  PROCESSES = '/dashboard/processes',
  CATALOGS = '/dashboard/catalogs',
  EMPLOYEES = '/dashboard/employees',
  CONFIRMATION_SUB = 'confirmation',
  GROUPS = '/dashboard/groups',
  CHAINS = '/dashboard/orderApprovalChains',
  LISTS_SUB = 'lists',
  RECIPIENTS = '/dashboard/recipients',
  CONTACTS = '/dashboard/contacts',
  WAREHOUSES = '/dashboard/warehouses',
  ANALYTICS = '/dashboard/analytics',
  ORDER_IMPORTS = '/dashboard/orderImports',
  PROJECTS = '/dashboard/projects',
  COUNTERPARTY_ORDERS = '/dashboard/counterparty-orders',
  EPD = '/dashboard/epd',
}

const CLIENT_SECTIONS: string[] = [
  MenuKeys.EPD,
  MenuKeys.PRODUCTS,
  MenuKeys.PROCESSES,
  MenuKeys.CATALOGS,
  MenuKeys.GROUPS,
  MenuKeys.CHAINS,
  MenuKeys.RECIPIENTS,
  MenuKeys.CONTACTS,
  MenuKeys.ANALYTICS,
  MenuKeys.ORDER_IMPORTS,
  MenuKeys.PROJECTS,
];

const COUNTERPARTY_SECTIONS: string[] = [MenuKeys.COUNTERPARTY_ORDERS, MenuKeys.EPD];

export const DashboardLayout: FC = () => {
  const navigate = useNavigate();
  const selectedKey = useLocation().pathname;
  const [collapsed, setCollapsed] = React.useState(false);
  const [activeRoute, setActiveRoute] = React.useState(selectedKey);
  const [defaultOpenKeys, setDefaultOpenKeys] = React.useState<MenuKeys[]>([]);
  const { t } = useTranslation(NSKeys.common);
  const { token } = theme.useToken();
  const currentSession = useLocalSelector((state) => state.currentSession);
  const isCounterparty = useLocalSelector(selectIsCounterpartySpace);
  const section = '/' + selectedKey.split('/').slice(1, 3).join('/');
  const isForeignSection = isCounterparty
    ? CLIENT_SECTIONS.includes(section) && !COUNTERPARTY_SECTIONS.includes(section)
    : COUNTERPARTY_SECTIONS.includes(section) && !CLIENT_SECTIONS.includes(section);
  const epdEnabled = stringToBoolean(process.env.REACT_APP_FEATURE_EPD);
  const canCountEpd =
    epdEnabled &&
    !!currentSession.ordersContext?.scope.$permissions.manage_epd &&
    !currentSession.data?.scope.is_sandbox;
  const epdCount = useEpdSigningCountQuery(canCountEpd);

  React.useEffect(() => {
    i18n.changeLanguage(currentSession.lang.toLowerCase());
    setupDayJs(currentSession.lang.toLowerCase());
  }, [currentSession.lang]);

  React.useEffect(() => {
    const [empty, url1, url2] = selectedKey.split('/');
    const res = '/' + [url1, url2].join('/');

    if ([MenuKeys.GROUPS, MenuKeys.CHAINS].some((key) => key === res)) {
      setDefaultOpenKeys([MenuKeys.CONFIRMATION_SUB]);
    }
    setActiveRoute(res);
  }, [selectedKey]);

  const handleIconClick = () => navigate('/');

  const USER_NAME =
    currentSession.data?.first_name || currentSession.data?.last_name
      ? `${currentSession.data?.first_name} ${currentSession.data?.last_name}`
      : '';

  const employeesItem = {
    label: t('menuCatalogEmployees'),
    icon: <IdcardOutlined />,
    key: MenuKeys.EMPLOYEES,
    disabled: !currentSession?.data?.scope?.$permissions?.view_users,
  };
  const warehousesItem = {
    label: t('menuWarehouses'),
    icon: <TruckOutlined />,
    key: MenuKeys.WAREHOUSES,
    disabled:
      !stringToBoolean(process.env.REACT_APP_FEATURE_WAREHOUSES) ||
      !currentSession?.warehousesContext?.scope?.$permissions?.view_warehouses,
  };
  const epdItem = {
    label: t('menuEpd'),
    icon: (
      <Badge count={canCountEpd && !epdCount.isError ? epdCount.data?.count : undefined} size="small" offset={[6, -2]}>
        <FileProtectOutlined />
      </Badge>
    ),
    key: MenuKeys.EPD,
    disabled: !epdEnabled || !currentSession.ordersContext?.scope.$permissions.view_epd,
  };
  const clientItems = [
    {
      label: t('menuTitleProducts'),
      icon: <ToolOutlined />,
      key: MenuKeys.PRODUCTS,
      disabled: !currentSession?.productsContext?.scope?.$permissions?.view_products,
    },
    {
      label: t('menuTitleOrders'),
      icon: <SnippetsOutlined />,
      key: MenuKeys.PROCESSES,
      disabled: !currentSession?.ordersContext?.scope?.$permissions?.view_orders,
    },
    epdItem,
    {
      label: t('menuCatalogs'),
      icon: <AppstoreOutlined />,
      key: MenuKeys.CATALOGS,
      disabled: !currentSession?.productsContext?.scope?.$permissions?.view_catalogs,
    },
    employeesItem,
    {
      label: t('menuTitleConfirmation'),
      icon: <BuildOutlined />,
      key: MenuKeys.CONFIRMATION_SUB,
      className: 'multiple',
      disabled:
        !currentSession?.data?.scope?.$permissions?.view_user_groups &&
        !currentSession?.data?.scope?.$permissions?.view_order_approval_chains,
      children: [
        {
          label: t('menuTitleGroups'),
          key: MenuKeys.GROUPS,
          disabled: !currentSession?.data?.scope?.$permissions?.view_user_groups,
        },
        {
          label: t('menuTitleChains'),
          key: MenuKeys.CHAINS,
          disabled: !currentSession?.data?.scope?.$permissions?.view_order_approval_chains,
        },
      ],
    },
    {
      label: t('menuTitleLists'),
      icon: <ProfileOutlined />,
      key: MenuKeys.LISTS_SUB,
      className: 'multiple',
      disabled:
        !currentSession?.dictionariesContext?.scope?.$permissions?.view_recipients &&
        !currentSession?.dictionariesContext?.scope?.$permissions?.view_contacts,
      children: [
        {
          label: t('menuTitleRecipients'),
          key: MenuKeys.RECIPIENTS,
          disabled: !currentSession?.dictionariesContext?.scope?.$permissions?.view_recipients,
        },
        {
          label: t('menuTitleContacts'),
          key: MenuKeys.CONTACTS,
          disabled: !currentSession?.dictionariesContext?.scope?.$permissions?.view_contacts,
        },
      ],
    },
    warehousesItem,
    {
      label: t('menuAnalytics'),
      icon: <BarChartOutlined />,
      key: MenuKeys.ANALYTICS,
      disabled:
        !stringToBoolean(process.env.REACT_APP_FEATURE_ANALYTICS) ||
        !currentSession?.data?.scope?.$permissions?.view_analytics,
    },
    {
      label: t('menuOrderImports'),
      icon: <ImportOutlined />,
      key: MenuKeys.ORDER_IMPORTS,
      disabled: !currentSession?.ordersContext?.scope?.$permissions?.view_import_orders,
    },
    {
      label: t('menuProjects'),
      icon: <FolderOpenOutlined />,
      key: MenuKeys.PROJECTS,
      disabled: !currentSession?.ordersContext?.scope?.$permissions?.view_projects,
    },
  ];
  const counterpartyItems = [
    {
      label: t('menuTitleOrders'),
      icon: <SnippetsOutlined />,
      key: MenuKeys.COUNTERPARTY_ORDERS,
      disabled: !currentSession?.ordersContext?.scope?.$permissions?.view_counterparty_orders,
    },
    epdItem,
    warehousesItem,
    employeesItem,
  ];

  return (
    <Container
      $colorBgBase={token.colorBgBase}
      $colorBgLayout={token.colorBgLayout}
      $colorPrimary={token.colorPrimary}
      $boxShadow={token.boxShadowSecondary}
      $colorBorder={token.colorBorderSecondary}>
      <Helmet>
        <title>{currentSession?.data?.scope.title}</title>
        <link rel="icon" type="image/png" href={currentSession?.data?.scope?.favicon?.variants?.small?.url || ''} />
      </Helmet>
      <Layout>
        <Layout.Sider width={209} collapsed={collapsed} onCollapse={setCollapsed}>
          <div>
            <LogoContainer>
              <WorkspaceLogo
                title={currentSession.scopeTitle}
                collapsed={collapsed}
                src={
                  collapsed
                    ? currentSession?.data?.scope?.favicon?.variants?.big?.url
                    : currentSession?.data?.scope?.logo?.variants?.small?.url
                }
                onClick={handleIconClick}
              />
            </LogoContainer>

            {isCounterparty && !collapsed && (
              <SpaceCaption>
                <Typography.Text strong ellipsis={{ tooltip: currentSession.data?.scope.title }}>
                  {currentSession.data?.scope.title}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('counterpartySpace')}
                </Typography.Text>
              </SpaceCaption>
            )}

            <UserMenu collapsed={collapsed} userName={USER_NAME} />
            <Menu
              mode="inline"
              onSelect={(item) => navigate(item.key)}
              // defaultActiveFirst={true}
              selectedKeys={[activeRoute]}
              key={defaultOpenKeys.length}
              defaultOpenKeys={defaultOpenKeys}
              items={isCounterparty ? counterpartyItems : clientItems}
            />
          </div>

          <Button
            style={{ marginLeft: 20, marginTop: 20 }}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
        </Layout.Sider>
        <Layout className="site-layout" style={{ marginLeft: collapsed ? 80 : 209 }}>
          <Suspense
            fallback={
              <SpinnerContainer>
                <Spin />
              </SpinnerContainer>
            }>
            {isForeignSection ? <AccessDenied /> : <Outlet />}
          </Suspense>
        </Layout>
      </Layout>
    </Container>
  );
};
